import { useRef } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import type { LDClient } from 'launchdarkly-js-client-sdk';

import useUI from 'src/hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useIsLdIdentifiedWithFlagsForDispensary } from 'src/utils/launch-darkly/identified-dispensary';

import { useAdsEnabled } from './use-ads-enabled';

export const HOME_SPONSORED_BRAND_CAROUSEL_FLAG = 'growth.ads.home-page-sponsored-brand-carousel.config';
export const HOME_SPONSORED_BRAND_PLACEMENT_FLAG = 'growth.ads.home-page-sponsored-brand-placement.config';

/**
 * Upper bound on a configured slot. A homepage lineup never has this many sections, so anything
 * larger is a misconfiguration and falls back to the historical top placement rather than pushing
 * the carousel to the very bottom of the page.
 */
export const MAX_SPONSORED_BRAND_SLOT = 20;

export type HomeSponsoredBrandPlacement = {
  /** The carousel is served to this store/shopper at all (ads enabled + carousel flag). */
  showCarousel: boolean;
  /** Render the hardcoded mount above the configurable lineup, i.e. today's behavior. */
  renderAtTop: boolean;
  /**
   * 1-based position within the *rendered* lineup sections, or null for the top mount.
   * `slot: 2` means "immediately after the first section that actually renders".
   */
  slot: number | null;
};

/**
 * Normalize a per-variant placement value into a slot below the top of the lineup.
 * Anything absent, malformed, or `slot: 1` means today's behavior (pinned above the lineup).
 */
function parseSlot(variantValue: unknown): number | null {
  if (typeof variantValue !== 'object' || variantValue === null) {
    return null;
  }

  const { slot } = variantValue as { slot?: unknown };

  if (typeof slot !== 'number' || !Number.isInteger(slot)) {
    return null;
  }

  if (slot < 2 || slot > MAX_SPONSORED_BRAND_SLOT) {
    return null;
  }

  return slot;
}

/** Reads a per-variant flag config from the client's own store, which never lags the signal. */
function readVariantConfig(client: LDClient | undefined, flagKey: string, variant: string): unknown {
  // `variation` is absent on partial test doubles and before the provider resolves its client.
  if (typeof client?.variation !== 'function') {
    return undefined;
  }
  const config = client.variation(flagKey, undefined) as Record<string, unknown> | null | undefined;
  return typeof config === 'object' && config !== null ? config[variant] : undefined;
}

/**
 * Single source of truth for where the homepage Sponsored Brand carousel renders.
 *
 * Call this once per page, in the home, and thread the resolved `slot` into
 * `HomepageCarouselSections`. One resolution per page is what makes the hardcoded top mount and
 * the in-lineup mount structurally mutually exclusive; rendering both would run two auctions and
 * emit two impression sets for one shopper. Two independent callers could each latch their own
 * value (see below) and disagree, so the slot is passed down rather than re-derived.
 *
 * Both the visibility flag and the placement flag are per-dispensary, so neither is trustworthy
 * until LaunchDarkly is actually serving this store's values. `useIsLdIdentifiedWithFlagsForDispensary`
 * is that check, and it gates visibility and placement alike: an untrustworthy read could both
 * show the carousel to a store outside the visibility cohort and place it using another store's
 * slot.
 *
 * Two weaker checks were tried and are deliberately not used. A bounded identify timeout also
 * expires while LD still holds the previous store's context after in-app navigation. And the
 * context key alone does not imply the flag *values* are that store's: `_app` seeds the initial
 * context with `ecomm-dispensary` from `window.reactEnv`, while the SSR bootstrap flags are
 * evaluated with a fixed internal context and cached across requests, so the key can match while
 * the values are still bootstrap values. Gating on identify completion covers both, and it is
 * reactive, which a render-time context read is not: LD emits no change event when an identify
 * swaps stores without changing an evaluated value.
 *
 * Both flag values are read from the LD client rather than from `useFlags()`, so the gate and the
 * values it admits come from the same place. The provider's flag state is updated by a `setState`
 * from LD's `change` event, which React schedules in the default lane, while the identify signal
 * reaches this hook through `useSyncExternalStore` and forces a synchronous re-render. A sync
 * render skips the pending default-lane update, so for one render the gate could say "serving
 * store B" while `useFlags()` still returned store A's values, and the latch below would freeze
 * A's placement for B. The client's own flag store is already current when `identify()` resolves,
 * so reading from it cannot tear against the signal. `useIsLdIdentifiedWithFlagsForDispensary`
 * owns the `useFlags()` subscription that re-renders this component when LD replaces the flag set.
 *
 * The first served placement is then latched per dispensary. `identify()` is async, so a flag
 * replacement can land after the carousel has mounted; without the latch that would move it,
 * remounting under a different parent and, with `cache-and-network`, running a second auction and
 * double-counting impressions. The latch is cleared whenever the dispensary id changes, including
 * a visit to a store that is not served the carousel at all, so returning to an earlier store
 * always re-resolves placement from that store's current flags.
 */
export function useHomeSponsoredBrandPlacement(): HomeSponsoredBrandPlacement {
  const UI = useUI();
  const client = useLDClient();
  const adsEnabled = useAdsEnabled();
  const { dispensary } = useDispensary();
  const dispensaryId = dispensary?.id;
  // Flags are only this store's flags once an identify has settled with this store's context.
  const isServingThisDispensary = useIsLdIdentifiedWithFlagsForDispensary(dispensaryId);

  // Only evaluate the flags once they belong to this store, so an evaluation is never attributed
  // to the wrong dispensary. One gate for visibility and placement: the carousel is served only to
  // a store confirmed to be in the cohort, and then mounts once, in its final position.
  const carouselEnabled = isServingThisDispensary
    ? Boolean(readVariantConfig(client, HOME_SPONSORED_BRAND_CAROUSEL_FLAG, UI.variant))
    : false;
  const showCarousel = Boolean(adsEnabled && carouselEnabled);

  // Latched placement for the dispensary currently being served.
  const latchedPlacement = useRef<{ dispensaryId: string | null | undefined; slot: number | null } | null>(null);

  // Reset outside the visibility gate: a store that is not served the carousel must still clear a
  // previous store's latch, otherwise returning to that store would reuse its stale placement.
  if (latchedPlacement.current !== null && latchedPlacement.current.dispensaryId !== dispensaryId) {
    latchedPlacement.current = null;
  }

  let slot = showCarousel
    ? parseSlot(readVariantConfig(client, HOME_SPONSORED_BRAND_PLACEMENT_FLAG, UI.variant))
    : null;

  if (showCarousel) {
    const latched = latchedPlacement.current ?? { dispensaryId, slot };
    latchedPlacement.current = latched;
    slot = latched.slot;
  }

  return {
    showCarousel,
    renderAtTop: showCarousel && slot === null,
    slot: showCarousel ? slot : null,
  };
}
